import React, { useState, useEffect, useContext } from "react";
import { withStyles } from "@material-ui/core";

import {
  getCountryExchanges,
  updateCountryExchange,
  addCountryExchange,
} from "../../../../../../API/Country";

import ModalDialog from "../ModalDialog";
import CountryExchangeForm from "./CountryExchangeForm";
import CountryExchangesTable from "./CountryExchangesTable";
import LoadingIndicator from "../../../../../../Component/LoadingIndicator";
import { recordTypes } from "../../../../../../API/config";
import { styles } from "./styles";
import ModalDialogContext from "../../../../../../Context/ModalDialog/ModalDialogContext";
import { useTranslation } from "react-i18next";

const CountryExchanges = (props) => {
  const { classes, hasPermission } = props;
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [formState, setFormState] = useState({});
  const [canEdit, setCanEdit] = useState();
  const [canAdd, setCanAdd] = useState();
  const [formErrors, setFormErrors] = useState({});

  const { showMessage, showErrorMessage, showLoadingMessage } =
    useContext(ModalDialogContext);
  const { t } = useTranslation();

  useEffect(() => {
    const evalPermissions = async () => {
      const canEdit = await hasPermission(
        recordTypes.countryExchange.name,
        recordTypes.countryExchange.actions.update.name,
      );
      setCanEdit(canEdit);
      const canAdd = await hasPermission(
        recordTypes.countryExchange.name,
        recordTypes.countryExchange.actions.create.name,
      );
      setCanAdd(canAdd);
    };

    evalPermissions();
  }, []);

  useEffect(() => {
    loadExchanges();
  }, []);

  const loadExchanges = async () => {
    try {
      setLoading(true);
      const result = await getCountryExchanges();
      const { data = [] } = result;
      setExchanges(data);
      setLoading(false);
    } catch (error) {
      showErrorMessage({ error });
      setLoading(false);
    }
  };

  const setOpenAddExchangeDialog = () => {
    setIsAddDialogOpen(true);
  };

  const closeAddExchangeDialog = () => {
    setIsAddDialogOpen(false);
  };

  const isInvalid = (exchange) => {
    const errors = {};
    setFormErrors(errors);

    let hasError = false;

    if (exchange.country_id === null || exchange.country_id === undefined) {
      errors["country_id"] = t("messages.country.countryNameError");
      hasError = true;
    }

    if (!exchange.exchange_name || typeof exchange.exchange_name !== "string") {
      errors["exchange_name"] = t("messages.country.exchangeNameError");
      hasError = true;
    }

    if (
      !exchange.exchange_code ||
      typeof exchange.exchange_code !== "string" ||
      exchange.exchange_code.length > 4
    ) {
      errors["exchange_code"] = t("messages.country.exchangeCodeError");
      hasError = true;
    }

    return hasError ? errors : null;
  };

  const submitNewExchange = async () => {
    try {
      setFormErrors({});
      const errors = isInvalid(formState);
      if (errors) {
        setFormErrors(errors);
        return;
      }

      showLoadingMessage();
      const data = {
        id: formState.id,
        countryId: formState.country_id,
        name: formState.exchange_name,
        code: formState.exchange_code,
        isEquivalent: formState.is_equivalent,
      };

      await addCountryExchange(data);
      await loadExchanges();

      setIsAddDialogOpen(false);

      showMessage({ message: t("messages.country.exchangeCreated") });
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const updateExchange = async (exchange) => {
    try {
      const errors = isInvalid(exchange);
      if (errors) {
        let message = "";
        Object.keys(errors).map(function (key, index) {
          message = message ? `${message}\n${errors[key]}` : `${errors[key]}`;
        });
        showMessage({
          message: message,
        });
        return false;
      }

      const data = {
        id: exchange.id,
        countryId: exchange.country_id,
        name: exchange.exchange_name,
        code: exchange.exchange_code,
        isEquivalent:
          exchange.is_equivalent === 1 || exchange.is_equivalent === "true"
            ? 1
            : 0,
      };

      await updateCountryExchange(data);
      await loadExchanges();

      showMessage({ message: t("messages.country.exchangeUpdated") });
      return true;
    } catch (error) {
      showErrorMessage({ error });
      return false;
    }
  };

  const onFieldChange = (value, name) => {
    setFormState((c) => {
      c[name] = value;
      return c;
    });
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  return (
    <>
      {loading ? (
        renderLoadingIndicator()
      ) : (
        <div className={classes.tableContainer}>
          <CountryExchangesTable
            loading={loading}
            exchanges={exchanges}
            onNewAction={setOpenAddExchangeDialog}
            onUpdateRecord={updateExchange}
            canAdd={canAdd}
            canEdit={canEdit}
          />
        </div>
      )}

      <ModalDialog
        open={isAddDialogOpen}
        onClose={closeAddExchangeDialog}
        onSubmit={submitNewExchange}
        title={t("labels.country.exchangeList.addNewExchange")}
      >
        <CountryExchangeForm
          onFieldChange={onFieldChange}
          formErrors={formErrors}
        />
      </ModalDialog>
    </>
  );
};

export default withStyles(styles, { withTheme: true })(CountryExchanges);
